import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { DeviceClasses, PanelState } from '@racemap/utilities/consts/events';
import { Col, Row } from 'antd';
import type { FC } from 'react';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useBrandsOfEvent } from 'src/store/events/events_reducers';
import { useWindowSize } from 'usehooks-ts';
import { BasicPanel } from '../../BasicComponents/BasicPanel/BasicPanel';
import { Center } from '../../BasicComponents/MetaComponent';
import { QRCode } from '../../BasicComponents/QRCode';
import { getRedeemKeyLink } from '../../utils/helpers';
import { PlayerButton } from '../TitleBar/Buttons';

interface Props {
  status: PanelState;
}

export const StartUpPanel: FC<Props> = ({ status }) => {
  const event = useCurrentEvent();
  const brands = useBrandsOfEvent(event?.id);
  const appName = brands[0]?.metadata.find((meta) => meta.default)?.shortApplicationName;
  const { width: windowWidth } = useWindowSize();
  const successState = status === PanelState.SUCCESS;

  if (event == null) return null;
  const { starters } = event;
  const startersArray = Array.from(starters?.values() || []);
  const appStarters = startersArray.filter(
    (starter) => starter.deviceClass === DeviceClasses.Mobile,
  );
  const startersCount = event.startersCount || 0;

  return (
    <div>
      <Section>
        <BasicPanel
          title={`Live tracking with ${appName != null ? appName : 'Racemap App '} right now`}
          defaultOpen={!successState || startersCount < 2}
          forceOpen={!successState || startersCount < 2}
        >
          <Row>
            <Col
              md={windowWidth > 1550 ? 12 : 24}
              style={{ display: 'flex', flexDirection: 'column', padding: '20px 17.5px' }}
            >
              {appStarters.length > 0 && startersCount < 2 && (
                <Subtitle>{appStarters[0].name}, you joined live tracking.</Subtitle>
              )}
              <Subtitle>
                Easily test your tracking map and show your location with{' '}
                {appName != null ? appName : 'Racemap App'}.
              </Subtitle>
              {appStarters.length < 1 ? (
                <Subtitle>
                  Scan the QR code or open the link to connect your phone for live tracking at your{' '}
                  {event.name} event.
                </Subtitle>
              ) : (
                <div>
                  <PlayerButton event={event} />
                </div>
              )}
              <Subtitle>For all your participants live tracking is that convenient.</Subtitle>
            </Col>
            {windowWidth >= 1550 && (
              <Col md={12} style={{ padding: '20px' }}>
                <QRCodeContainer>
                  <QRCode isClickable={true} value={getRedeemKeyLink(event.passcode, brands[0])} />
                </QRCodeContainer>
              </Col>
            )}
          </Row>
          {windowWidth < 1550 && (
            <Row>
              <Col md={24} style={{ padding: '20px' }}>
                <QRCodeContainer>
                  <QRCode isClickable={true} value={getRedeemKeyLink(event.passcode, brands[0])} />
                </QRCodeContainer>
              </Col>
            </Row>
          )}
        </BasicPanel>
      </Section>
    </div>
  );
};

const Subtitle = styled.div`
  font-size: large;
  color: ${RacemapColors.DarkGray};
  margin-bottom: 0.5em;
`;

const QRCodeContainer = styled(Center)`
  width: 100%;
  height: 100%;
`;

const Section = styled.div`
  padding: 1em 0;
  border: none;
`;
